import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, User, ArrowRight, AlertCircle } from 'lucide-react';
import axios from 'axios';
import AdminDashboard from './AdminDashboard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const [token, setToken] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const saved = sessionStorage.getItem('admin_token');
    if (saved) {
      setToken(saved);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await axios.post(`${API_URL}/api/admin/login`, { username, password });
      sessionStorage.setItem('admin_token', res.data.token);
      setToken(res.data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    setToken(null);
    setUsername('');
    setPassword('');
  };

  if (token) {
    return <AdminDashboard token={token} onLogout={handleLogout} />;
  }

  return (
    <div className="min-h-screen bg-[#162033] flex items-center justify-center px-6">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-violet-500/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-400 to-emerald-400 flex items-center justify-center mx-auto mb-4">
            <span className="font-unbounded font-bold text-void text-2xl">M</span>
          </div>
          <h1 className="font-unbounded font-bold text-2xl text-white mb-2">
            Administration
          </h1>
          <p className="font-jakarta text-sm text-slate-500">
            Connectez-vous pour accéder au panneau d'administration
          </p>
        </div>

        {/* Login Form */}
        <form 
          onSubmit={handleLogin}
          className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-8"
        >
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 p-3 rounded-xl bg-red-500/15 text-red-400 border border-red-500/20 mb-6 text-sm"
            >
              <AlertCircle className="w-4 h-4" />
              {error}
            </motion.div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block font-jakarta text-xs text-slate-500 mb-2 uppercase tracking-wider">
                Identifiant
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-void/60 border border-white/[0.08] text-white placeholder-slate-600 focus:border-cyan-500/40 focus:outline-none transition-colors text-sm"
                  placeholder="admin"
                  data-testid="admin-username"
                />
              </div>
            </div>

            <div>
              <label className="block font-jakarta text-xs text-slate-500 mb-2 uppercase tracking-wider">
                Mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-void/60 border border-white/[0.08] text-white placeholder-slate-600 focus:border-cyan-500/40 focus:outline-none transition-colors text-sm"
                  placeholder="••••••••••"
                  data-testid="admin-password"
                />
              </div>
            </div>
          </div>

          <motion.button
            type="submit"
            disabled={loading}
            className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-500 text-void font-semibold text-sm hover:shadow-lg hover:shadow-cyan-500/20 transition-all disabled:opacity-50"
            whileHover={{ scale: loading ? 1 : 1.01 }}
            whileTap={{ scale: loading ? 1 : 0.99 }}
            data-testid="admin-login-btn"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-void/30 border-t-void rounded-full animate-spin" />
                Connexion...
              </>
            ) : (
              <>
                Se connecter
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </motion.button>
        </form>

        {/* Back link */}
        <div className="text-center mt-6">
          <a 
            href="/" 
            className="font-jakarta text-sm text-slate-500 hover:text-cyan-400 transition-colors"
          >
            ← Retour au site
          </a>
        </div>
      </motion.div>
    </div>
  );
}
